import type {
  ProgressiveDiscount,
  ProgressiveDiscountTier,
} from '../types/product'

type ProgressiveDiscountPricingInput = {
  progressiveDiscount?: ProgressiveDiscount
  itemId: string | number
  quantity: number
}

type ProgressiveDiscountPricing = {
  activeTier: ProgressiveDiscountTier | null
  discountPercentage: number
  discountedUnitPrice: number | null
  originalUnitPrice: number | null
  totalPrice: number | null
  discountAmount: number
}

const EMPTY_PROGRESSIVE_PRICING: ProgressiveDiscountPricing = {
  activeTier: null,
  discountPercentage: 0,
  discountedUnitPrice: null,
  originalUnitPrice: null,
  totalPrice: null,
  discountAmount: 0,
}

function sortTiersByQuantity(tiers: ProgressiveDiscountTier[]) {
  return [...tiers].sort((a, b) => a.quantity - b.quantity)
}

export function isListPriceHigherThan(listPrice: number, currentPrice: number) {
  return Math.round(listPrice * 100) > Math.round(currentPrice * 100)
}

export function dedupeProgressiveTiers(tiers: ProgressiveDiscountTier[]) {
  const tiersByQuantity = new Map<number, ProgressiveDiscountTier>()

  for (const tier of tiers) {
    const current = tiersByQuantity.get(tier.quantity)
    const keepIncomingTier =
      !current || (tier.bestOption && !current.bestOption)

    if (keepIncomingTier) {
      tiersByQuantity.set(tier.quantity, tier)
    }
  }

  return sortTiersByQuantity([...tiersByQuantity.values()])
}

export function getActiveProgressiveTier(
  tiers: ProgressiveDiscountTier[],
  quantity: number
): ProgressiveDiscountTier | null {
  if (!tiers.length) {
    return null
  }

  const sorted = sortTiersByQuantity(tiers)
  const maxTier = sorted[sorted.length - 1]

  if (quantity >= maxTier.quantity) {
    return maxTier
  }

  let activeTier = sorted[0]

  for (const tier of sorted) {
    if (quantity >= tier.quantity) {
      activeTier = tier
    }
  }

  return activeTier
}

export function getProgressiveDiscountPricing({
  progressiveDiscount,
  itemId,
  quantity,
}: ProgressiveDiscountPricingInput): ProgressiveDiscountPricing {
  const skuProgressiveDiscount = progressiveDiscount?.items.find(
    (item) => String(item.itemId) === String(itemId)
  )

  if (!skuProgressiveDiscount) {
    return EMPTY_PROGRESSIVE_PRICING
  }

  const sortedTiers = dedupeProgressiveTiers(skuProgressiveDiscount.tiers)
  const [baseTier] = sortedTiers

  const activeTier = getActiveProgressiveTier(sortedTiers, quantity)

  if (!activeTier) {
    return EMPTY_PROGRESSIVE_PRICING
  }

  const { discountPercentage } = activeTier
  const discountedUnitPrice = activeTier.unitPrice

  const originalUnitPrice = baseTier?.unitPrice ?? discountedUnitPrice

  const totalPrice = activeTier.totalPrice || discountedUnitPrice * quantity
  const discountAmount = Math.max(originalUnitPrice - discountedUnitPrice, 0)

  return {
    activeTier,
    discountPercentage,
    discountedUnitPrice,
    originalUnitPrice,
    totalPrice,
    discountAmount,
  }
}

/**
 * O tier base (sem desconto) não conta como desconto progressivo aplicado: o
 * progressivo não acumula com o à vista (PIX), então só prevalece quando o tier
 * ativo tem desconto real. No tier base vale o preço normal (PLP/carrinho).
 */
export function hasAppliedProgressiveDiscount(
  pricing: ProgressiveDiscountPricing
) {
  return (
    !!pricing.activeTier &&
    pricing.discountedUnitPrice !== null &&
    (pricing.discountPercentage > 0 || pricing.discountAmount > 0)
  )
}
